import { BxError, ProductCondition } from "@boxem/core-api-schema";
import { createContext, useContext, useEffect, useState } from "react";
import { Product, ProductPatch, SellerProductData, useInventoryContext } from "./InventoryContext";
import { createSupplier, pollFbaListings, submitFbaListings } from "@/api/fulfillment";
import { useErrorModalContext } from "./ErrorModalContext";
import { useLocation } from "wouter";
import { LocalStorageKeys, getLocalStorageItem, setLocalStorageItem } from "@/utils/localStorageUtils";

export type FbaListingIssue = {
  code: string;
  message: string;
  severity: "ERROR" | "WARNING" | "INFO",
  attributeNames?: string[];
  categories: ("INVALID_ATTRIBUTE" | "MISSING_ATTRIBUTE" | "INVALID_IMAGE" | "MISSING_IMAGE" | "INVALID_PRICE" | "MISSING_PRICE" | "DUPLICATE" | "QUALIFICATION_REQUIRED")[];
  enforcements?: {
    actions: ("LISTING_SUPPRESSED" | "ATTRIBUTE_SUPPRESSED" | "CATALOG_ITEM_REMOVED" | "SEARCH_SUPPRESSED")[];
    exemption: {
      status: "EXEMPT" | "EXEMPT_UNTIL_EXPIRY_DATE" | "NOT_EXEMPT";
      expiryDate?: string;
    }
  }
};

export type FbaListingPatch = {
  sku?: string;
  priceUSD?: string;
  condition?: ProductCondition;
  conditionNotes?: string;
  buyCost?: string;
  datePurchased?: string;
  expirationDate?: string;
  status?: string;
  supplier?: string;
  issues?: FbaListingIssue[]; //These are errors we get back from Amazon.
  errors?: BxError[]; //These are errors that come from our API's.
};

export type FbaListingInput = {
  name: string,
  asin: string;
  sku: string;
  image: string;
  category: string;
  priceUSD: string;
  condition: ProductCondition;
  supplier: string;
  conditionNotes?: string;
  buyCost?: string;
  datePurchased?: string;
  expirationDate?: string;
  issues: FbaListingIssue[];
  errors: BxError[];
  submittedAt: string;
};

export type FbaListing = {
  name: string,
  asin: string;
  sku: string;
  image: string;
  category: string;
  priceUSD: string;
  condition: ProductCondition;
  supplier: string;
  conditionNotes?: string;
  buyCost?: string;
  datePurchased?: string;
  expirationDate?: string;
  status?: string;
  issues: FbaListingIssue[];
  errors: BxError[];
  submittedAt: string;
};

type FbaListingContextProps = {
  fbaListingFlowStep: number;
  fbaListings: FbaListing[];
  submitLoading: boolean;
  progressFbaListingFlow: () => void;
  resetFbaListingFlow: () => void;
  addListingToList: (input: FbaListingInput) => void;
  editListingInList: (sku: string, patch: FbaListingPatch) => void;
  deleteListingFromList: (sku: string) => void;
  addSupplier: (name: string) => void;
  submitListings: () => void;
  startFbaListingPolling: () => void;
};

const FbaListingContext = createContext<FbaListingContextProps>(undefined);
export const useFbaListingContext = () => useContext(FbaListingContext);

let fbaListingPollingId: ReturnType<typeof setInterval>;

let globalFbaPollingIds: string[] = [];

export const FbaListingContextProvider = ({
  children,
}: { children: React.ReactNode }) => {
  const [location, navigate] = useLocation();
  const { suppliers, setSuppliers, addProductToInventory, editProductInInventory, inventoryFlat } = useInventoryContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const [fbaListings, setFbaListings] = useState<FbaListing[]>(() => {
    return getLocalStorageItem(LocalStorageKeys.fbaListings, []);
  });
  const [fbaListingFlowStep, setFbaListingFlowStep] = useState<number>(() => {
    return getLocalStorageItem(LocalStorageKeys.fbaListingsFlowStep, 0);
  });
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [listingsPollingNum, setListingPollingNum] = useState<number>();

  const addSupplier = (name: string) => {
    //Check if supplier already exists. If it DOESN'T then create the supplier and add it to the list of suppliers.
    const supplerDoesntExist = suppliers.findIndex(sup => sup.name === name) === -1;
    if (supplerDoesntExist) createSupplier(name).then(sup => {
      setSuppliers([ ...suppliers, sup ]);
    }).catch(err => handleErrorResponseGenerically(err));
  };

  const progressFbaListingFlow = () => {
    const step = fbaListingFlowStep + 1;
    setFbaListingFlowStep(step);
    setLocalStorageItem(LocalStorageKeys.fbaListingsFlowStep, step);
  };

  const resetFbaListingFlow = () => {
    setFbaListingFlowStep(0);
    setFbaListings([]);
    setSubmitLoading(false);
    setLocalStorageItem(LocalStorageKeys.fbaListings, []);
    setLocalStorageItem(LocalStorageKeys.fbaListingsFlowStep, 0);
  };

  const addListingToList = (input: FbaListingInput) => {
    const listings = [ ...fbaListings, input ];
    setFbaListings(listings);
    setLocalStorageItem(LocalStorageKeys.fbaListings, listings);
  };

  const editListingInList = (sku: string, patch: FbaListingPatch): FbaListing => {
    const listingIndex = fbaListings.findIndex(listing => listing.sku === sku);

    fbaListings[listingIndex] = {
      ...fbaListings[listingIndex],
      ...patch,
    };

    //Force the mutation
    setFbaListings([...fbaListings]);
    setLocalStorageItem(LocalStorageKeys.fbaListings, [...fbaListings]);

    return fbaListings[listingIndex];
  };

  const deleteListingFromList = (sku: string) => {
    const filteredListings = fbaListings.filter(listing => listing.sku !== sku);
    setFbaListings(filteredListings);
    setLocalStorageItem(LocalStorageKeys.fbaListings, filteredListings);
  };

  const submitListings = () => {
    setSubmitLoading(true);

    submitFbaListings(fbaListings)
      .then(listings => {

        listings.map((listing) => {
          
          //We can use the index here since the listings returned from the API are in the same order as they were when they were submitted.
          //Here we hydrate the listings we already have in state with the issues and errors we get back from the API.
          const listingPatch: FbaListingPatch = {
            status: listing.data.status,
            issues: listing.data.issues,
            errors: listing.errors,
          };

          const submittedListing = editListingInList(listing.data.sku, listingPatch);
          //Now we need to add the item to the inventory.

          const product = new Product();
          product.name = submittedListing.name;
          product.asin = submittedListing.asin;
          product.image = submittedListing.image;
          product.status = "PROCESSING";
          product.issues = submittedListing.issues as unknown as string;
          product.category = submittedListing.category;
          product.buyCost = submittedListing.buyCost;
          product.sellerData = [
            {
              sku: submittedListing.sku,
              listPrice: parseFloat(submittedListing.priceUSD),
              condition: submittedListing.condition,
              quantity: 0,
              isFBM: false,
              buyCost: submittedListing.buyCost,
              status: "PROCESSING",
              issues: submittedListing.issues as unknown as string,
              submittedAt: submittedListing.submittedAt,
            }
          ] as SellerProductData[];
          //Create the search string for the product.
          product.makeSearchString();

          addProductToInventory(product);
        });

        //We have a seperate "then" here so the context state can update with the new product before we start polling.
        //Progress flow step to step 2 and start polling for issues
        progressFbaListingFlow();
      }).finally(() => setSubmitLoading(false));
  };

  const handlePolledListing = (listing: {
    sku: string;
    issues: unknown;
    status: string;
  }) => {
    //Update the product in the FbaInventory
    const patch: ProductPatch = {
      status: listing.status,
      issues: listing.issues as string,
    };
    editProductInInventory(listing.sku, patch);
  };

  const startFbaListingPolling = async () => {
    //Clear the current interval and create a new one.
    if (fbaListingPollingId) {
      clearInterval(fbaListingPollingId);
      fbaListingPollingId = undefined;
    }

    //Don't start polling if there aren't any skus that need to be polled.
    if (globalFbaPollingIds.length === 0) {
      return;
    }
    
    await pollFbaListings(globalFbaPollingIds).then(listings => {
      listings.map(listing => handlePolledListing(listing));

      if (globalFbaPollingIds.length > 0 && !fbaListingPollingId) {
        fbaListingPollingId = setInterval(async () => {
          if (globalFbaPollingIds.length === 0) {
            clearInterval(fbaListingPollingId);
            fbaListingPollingId = undefined;
            return;
          }

          await pollFbaListings(globalFbaPollingIds).then(listings => {
            listings.map(listing => handlePolledListing(listing));

            if (globalFbaPollingIds.length === 0) {
              clearInterval(fbaListingPollingId);
              fbaListingPollingId = undefined;
              return;
            }
          });
        }, 35_000); //Run every 35 seconds...
      }
    });
  };

  useEffect(() => {
    if (inventoryFlat.length > 0) {
      globalFbaPollingIds = inventoryFlat.filter(product => product.status === "PROCESSING" && !product.sellerData[0].isFBM).map(p => p.sellerData[0].sku);
      setListingPollingNum(globalFbaPollingIds.length);
    }
  }, [inventoryFlat]);

  useEffect(() => {
    startFbaListingPolling();
  }, [listingsPollingNum]);

  useEffect(() => {
    if (location !== "/fba/listings/create") resetFbaListingFlow();
  }, [location]);

  return (
    <FbaListingContext.Provider
      value={{
        fbaListingFlowStep,
        fbaListings,
        submitLoading,
        progressFbaListingFlow,
        resetFbaListingFlow,
        addListingToList,
        editListingInList,
        deleteListingFromList,
        addSupplier,
        submitListings,
        startFbaListingPolling,
      }}
    >
      {children}
    </FbaListingContext.Provider>
  );
};